import { cn } from '@/lib/utils'

interface CropMarksProps {
  color?: string
  inset?: string | number
  size?: number
  className?: string
}

type Corner = 'tl' | 'tr' | 'bl' | 'br'

const PATHS: Record<Corner, (s: number) => string> = {
  tl: (s) => `M${s} 0 H0 V${s}`,
  tr: (s) => `M0 0 H${s} V${s}`,
  bl: (s) => `M0 ${s} H0 V0 H${s}`,  // corrected: bottom-left goes up and right
  br: (s) => `M0 ${s} H${s} V0`,
}

const POSITIONS: Record<Corner, (inset: string) => React.CSSProperties> = {
  tl: (i) => ({ top: i, left: i }),
  tr: (i) => ({ top: i, right: i }),
  bl: (i) => ({ bottom: i, left: i }),
  br: (i) => ({ bottom: i, right: i }),
}

function CornerMark({
  corner,
  color,
  size,
  inset,
}: {
  corner: Corner
  color: string
  size: number
  inset: string
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      aria-hidden="true"
      style={{
        position: 'absolute',
        ...POSITIONS[corner](inset),
        pointerEvents: 'none',
      }}
    >
      <path d={PATHS[corner](size)} stroke={color} strokeWidth="0.75" />
    </svg>
  )
}

export default function CropMarks({
  color = 'currentColor',
  inset = 16,
  size = 12,
  className,
}: CropMarksProps) {
  const insetStr = typeof inset === 'number' ? `${inset}px` : inset
  const corners: Corner[] = ['tl', 'tr', 'bl', 'br']

  // Opacity baked into individual SVGs — no wrapper opacity so no compositing
  // layer boundary that would create a visible seam at the hero's box edge.
  return (
    <div
      aria-hidden="true"
      className={cn(className)}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
      }}
    >
      {corners.map((c) => (
        <CornerMark key={c} corner={c} color={color} size={size} inset={insetStr} />
      ))}
    </div>
  )
}
